// Pure, framework-free logic for the passwordless "magic link" login (see
// `features/passwordless-login.md`). The opaque token is generated and hashed
// server-side (secure randomness / keyed hashing are not domain concerns — they
// live in `server/loginLink.ts`, mirroring `server/otp.ts`); this module owns
// the shared, testable rules around the challenge — its lifetime and
// single-use semantics — plus the URL shape both sides agree on.
//
// Kept import-free so it is shared by the API server (which issues and redeems
// links) and the browser (which reads the token out of the verify URL) alike.

/** How long a delivered login link stays valid (5 minutes). */
export const LOGIN_LINK_TTL_MS = 5 * 60 * 1000;

/**
 * Client route the emailed login link points at. The SPA serves its shell for
 * this extension-less path (see `serveStatic` in `server/index.ts`), then reads
 * the token out of the query string and redeems it.
 */
export const LOGIN_LINK_ROUTE = '/login';

/** Query-string parameter carrying the one-time token on the verify URL. */
export const LOGIN_TOKEN_PARAM = 'token';

/**
 * A pending passwordless login link. Only the keyed *hash* of the token is ever
 * persisted (never the token itself), so a database leak cannot mint a live
 * link — the same discipline as `OtpChallenge`. `consumedAt` marks a redeemed
 * (single-use) link; `expiresAt`/`createdAt` are epoch milliseconds.
 */
export interface LoginLinkChallenge {
  /** Opaque row id (a `Login_<uuid>`), distinct from the secret token. */
  id: string;
  /** The user the link authenticates as. */
  userId: string;
  /** Keyed hash (HMAC) of the delivered token — the only stored form. */
  tokenHash: string;
  expiresAt: number;
  /** When the link was redeemed (epoch ms); null while still pending. */
  consumedAt: number | null;
  createdAt: number;
}

/**
 * Build a fresh login-link challenge for `userId` from an already-computed
 * `tokenHash`. `now` (epoch ms) is injectable for deterministic tests and sets
 * both `createdAt` and the `expiresAt = now + LOGIN_LINK_TTL_MS` deadline.
 */
export function createLoginLinkChallenge(
  id: string,
  userId: string,
  tokenHash: string,
  now: number = Date.now(),
): LoginLinkChallenge {
  return {
    id,
    userId,
    tokenHash,
    expiresAt: now + LOGIN_LINK_TTL_MS,
    consumedAt: null,
    createdAt: now,
  };
}

/** Whether the link has passed its expiry. */
export function isLoginLinkExpired(
  link: Pick<LoginLinkChallenge, 'expiresAt'>,
  now: number = Date.now(),
): boolean {
  return now >= link.expiresAt;
}

/** Whether the link may still be redeemed: neither consumed nor expired. */
export function isLoginLinkUsable(
  link: Pick<LoginLinkChallenge, 'expiresAt' | 'consumedAt'>,
  now: number = Date.now(),
): boolean {
  return link.consumedAt === null && !isLoginLinkExpired(link, now);
}

/** Trim a single trailing slash so origin + route never doubles it. */
function trimTrailingSlash(origin: string): string {
  return origin.endsWith('/') ? origin.slice(0, -1) : origin;
}

/**
 * The absolute URL emailed to the user. `origin` is the public app origin (e.g.
 * `https://athlete-hub.app`); the token travels as the `token` query parameter
 * on the `/login` route. Pure string building — no `URL` global — so it stays
 * shared between client and server.
 */
export function buildLoginLinkUrl(origin: string, token: string): string {
  return `${trimTrailingSlash(origin)}${LOGIN_LINK_ROUTE}?${LOGIN_TOKEN_PARAM}=${encodeURIComponent(token)}`;
}
